import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useFormContext } from 'react-hook-form';

import { TextFormField } from '~components/FormFields/TextFormField';
import { NoteTypes } from '~constants/enums';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { Project } from '~hooks/useProjects/models';

enum NoteFieldNames {
  ORDER = 'orderNotes',
  JOB = 'jobNotes',
  INTERNAL = 'internalNotes',
  PROJECT = 'projectNotes',
}

interface NotesProps {
  defaultData?: Project | Order | Job | null;
  defaultTab: NoteTypes;
}

export const Notes = ({ defaultTab }: NotesProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const isFieldDisabled = (noteType: NoteTypes): boolean => {
    switch (noteType) {
      case NoteTypes.PROJECT:
        return defaultTab !== NoteTypes.PROJECT;
      case NoteTypes.ORDER:
        return defaultTab !== NoteTypes.ORDER && defaultTab !== NoteTypes.PROJECT;
      case NoteTypes.JOB:
        return false;
      case NoteTypes.INTERNAL:
        return false;
      default:
        return false;
    }
  };

  const labelStyles = {
    width: '100px',
    pt: 1,
  };

  return (
    <Card sx={{ mt: 2, p: 2 }}>
      <Typography
        variant="form-section-title"
        sx={{ mb: 2 }}
      >{`${t('form_fields.notes')}`}</Typography>

      <Box sx={{ width: '100%' }}>
        {/* Project Notes */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="subtitle2" sx={labelStyles}>
            {t('form_fields.project')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextFormField
              control={control}
              errors={errors}
              name={NoteFieldNames.PROJECT}
              label={''}
              sx={{ width: 'auto' }}
              multiline={true}
              rows={3}
              isRequired={false}
              disabled={isFieldDisabled(NoteTypes.PROJECT)}
              placeholder={t('form_fields.project_notes_placeholder')}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Order Notes */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="subtitle2" sx={labelStyles}>
            {t('form_fields.order')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextFormField
              control={control}
              errors={errors}
              name={NoteFieldNames.ORDER}
              label={''}
              sx={{ width: 'auto' }}
              multiline={true}
              rows={3}
              isRequired={false}
              disabled={isFieldDisabled(NoteTypes.ORDER)}
              placeholder={t('form_fields.order_notes_placeholder')}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Driver Notes */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="subtitle2" sx={labelStyles}>
            {t('form_fields.driver')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextFormField
              control={control}
              errors={errors}
              name={NoteFieldNames.JOB}
              label={''}
              sx={{ width: 'auto' }}
              multiline={true}
              rows={3}
              isRequired={false}
              disabled={isFieldDisabled(NoteTypes.JOB)}
              placeholder={t('form_fields.driver_notes_placeholder')}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Internal Notes */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
          <Typography variant="subtitle2" sx={labelStyles}>
            {t('form_fields.internal')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextFormField
              control={control}
              errors={errors}
              name={NoteFieldNames.INTERNAL}
              label={''}
              sx={{ width: 'auto' }}
              multiline={true}
              rows={3}
              isRequired={false}
              disabled={isFieldDisabled(NoteTypes.INTERNAL)}
              placeholder={t('form_fields.internal_notes_placeholder')}
            />
          </Box>
        </Box>
      </Box>
    </Card>
  );
};
